import React, { useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

const difficultyColor = (value) => {
  if (value === 'Easy') return 'success';
  if (value === 'Medium') return 'warning';
  if (value === 'Hard') return 'error';
  return 'default';
};

const statusColor = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'accepted') return 'success';
  if (!normalized) return 'default';
  return 'error';
};

const extractDateValue = (valueOrParams) => {
  if (valueOrParams && typeof valueOrParams === 'object' && 'value' in valueOrParams) {
    return valueOrParams.value;
  }
  return valueOrParams;
};

const formatDate = (valueOrParams) => {
  const rawValue = extractDateValue(valueOrParams);
  if (!rawValue) return '-';
  const date = new Date(rawValue);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const NoRowsOverlay = () => (
  <Box sx={{ p: 2 }}>
    <Typography variant="body2" color="text.secondary">
      No submissions found
    </Typography>
  </Box>
);

const SubmissionTable = ({
  rows = [],
  loading = false,
  page = 1,
  pageSize = 10,
  total = 0,
  onPageChange,
  onRowClick,
}) => {
  const columns = useMemo(
    () => [
      { field: 'problem_title', headerName: 'Problem', flex: 1.2, minWidth: 220 },
      {
        field: 'difficulty',
        headerName: 'Difficulty',
        minWidth: 130,
        renderCell: ({ value }) => (
          <Chip label={value || '-'} size="small" color={difficultyColor(value)} />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 150,
        renderCell: ({ value }) => (
          <Chip label={value || '-'} size="small" color={statusColor(value)} />
        ),
      },
      { field: 'language', headerName: 'Language', minWidth: 120, valueGetter: (_, row) => row.language || '-' },
      { field: 'runtime', headerName: 'Runtime', minWidth: 110, valueGetter: (_, row) => row.runtime || '-' },
      { field: 'memory', headerName: 'Memory', minWidth: 110, valueGetter: (_, row) => row.memory || '-' },
      {
        field: 'submittedAt',
        headerName: 'Submitted At',
        minWidth: 190,
        valueGetter: (_, row) => row.submittedAt || row.submission_time || null,
        valueFormatter: (value) => formatDate(value),
      },
    ],
    []
  );

  const normalizedRows = useMemo(
    () =>
      (rows || []).map((row, index) => ({
        id: row.id || row._id || `${index}-${row.submittedAt || ''}`,
        ...row,
      })),
    [rows]
  );

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        autoHeight
        rows={normalizedRows}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        paginationMode="server"
        rowCount={total}
        pageSizeOptions={[10, 20, 50]}
        paginationModel={{ page: Math.max(page - 1, 0), pageSize }}
        onPaginationModelChange={(model) => {
          if (onPageChange) onPageChange(model.page + 1, model.pageSize);
        }}
        onRowClick={(params) => {
          if (!onRowClick) return;
          onRowClick(params.row);
        }}
        rowHeight={44}
        columnHeaderHeight={48}
        slots={{ noRowsOverlay: NoRowsOverlay }}
      />
    </Box>
  );
};

export default SubmissionTable;
